import React, { useState, useEffect } from 'react';

interface Session {
  sessionId: string;
  sessionName: string;
  originalFilename: string;
  selectedFields: any[]; // This comes as an array from the backend, not a JSON string
  deploymentStatus: string;
  revisionNumber: number;
  updatedAt: string;
  description?: string; // Optional description field (up to 400 characters)
  changeComment?: string; // Optional change comment from latest revision
  createdByUserEmail?: string; // Email of the user who created the session (null if unauthenticated at save time)
}

interface LoadSessionDialogProps {
  isOpen: boolean;
  isLoading: boolean;
  sessions: Session[];
  onClose: () => void;
  onLoadSession: (sessionId: string) => void;
  currentPdfFilename?: string; // Auto-filter to current PDF when provided
}

const LoadSessionDialog: React.FC<LoadSessionDialogProps> = ({
  isOpen,
  isLoading,
  sessions,
  onClose,
  onLoadSession,
  currentPdfFilename
}) => {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [dialogSize, setDialogSize] = useState({ width: 1000, height: 600 });
  const [isResizing, setIsResizing] = useState(false);
  const [hoveredSession, setHoveredSession] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [searchTerm, setSearchTerm] = useState('');

  // Clear the hover tooltip whenever the dialog closes. This component self-hides via
  // `if (!isOpen) return null` but STAYS MOUNTED, so without this reset `hoveredSession`
  // lingers (a row's mouseleave never fires when the dialog closes out from under it) and
  // the tooltip pops the instant the dialog is reopened.
  useEffect(() => {
    if (!isOpen) {
      setHoveredSession(null);
    }
  }, [isOpen]);

  // Unified search: substring (case-insensitive) matches session name OR creator's email.
  // Per project_session_multi_user_search V1.1 vision — works for company tiers with
  // multiple users. Client-side for now; backend repo method exists for future server-side.
  const matchesSearch = (session: Session, term: string): boolean => {
    if (!term.trim()) return true;
    const needle = term.trim().toLowerCase();
    const name = (session.sessionName || '').toLowerCase();
    const email = (session.createdByUserEmail || '').toLowerCase();
    return name.includes(needle) || email.includes(needle);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = dialogSize.width;
    const startHeight = dialogSize.height;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(800, Math.min(1400, startWidth + (e.clientX - startX)));
      const newHeight = Math.max(500, Math.min(800, startHeight + (e.clientY - startY)));
      setDialogSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleSessionMouseEnter = (sessionId: string, e: React.MouseEvent) => {
    setHoveredSession(sessionId);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.right + 10,
      y: rect.top
    });
  };

  const handleSessionMouseLeave = () => {
    setHoveredSession(null);
  };

  const formatTimestamp = (timestamp: string): string => {
    if (!timestamp) return 'Unknown';
    // Backend sends LocalDateTime without timezone — treat as UTC
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    const date = new Date(utcTimestamp);
    if (isNaN(date.getTime())) return 'Unknown';

    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 0) return date.toLocaleString();
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get unique PDF filenames for filtering
  const uniquePdfFiles = Array.from(new Set(sessions.map(s => s.originalFilename || 'Unknown')));
  const [pdfFilter, setPdfFilter] = useState<string>(currentPdfFilename || 'all');

  const getDeploymentStatusStyle = (status: string) => {
    switch (status) {
      case 'planning':
        return 'bg-neutral-100 text-neutral-700 border-neutral-300';
      case 'development':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'testing':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'release':
        return 'bg-green-100 text-green-700 border-green-300';
      default:
        return 'bg-neutral-100 text-neutral-700 border-neutral-300';
    }
  };

  const getDeploymentStatusIcon = (status: string) => {
    switch (status) {
      case 'planning':
        return '🗂️';
      case 'development':
        return '⚙️';
      case 'testing':
        return '🧪';
      case 'release':
        return '🚀';
      default:
        return '📄';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white rounded-2xl shadow-2xl overflow-hidden relative select-none"
        style={{ 
          width: `${dialogSize.width}px`, 
          height: `${dialogSize.height}px`,
          cursor: isResizing ? 'nw-resize' : 'default'
        }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 bg-gradient-to-r from-emerald-50 to-emerald-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-display font-semibold text-neutral-900">Load Session</h2>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Two-Panel Content Layout */}
        <div className="flex" style={{ height: `${dialogSize.height - 140}px` }}>
          {/* Left Panel: Session Tree */}
          <div className="w-1/3 border-r border-neutral-200 p-4 overflow-y-auto">
            <h3 className="text-sm font-semibold text-neutral-700 mb-2 uppercase tracking-wide">Saved Sessions</h3>

            {/* Unified search: matches session name OR creator email */}
            <div className="mb-3">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or user…"
                className="w-full text-xs px-2 py-1.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {/* PDF Filter */}
            {uniquePdfFiles.length > 1 && (
              <div className="mb-3">
                <select
                  value={pdfFilter}
                  onChange={(e) => setPdfFilter(e.target.value)}
                  className="w-full text-xs px-2 py-1.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="all">All PDFs ({sessions.length})</option>
                  {uniquePdfFiles.map(filename => {
                    const count = sessions.filter(s => (s.originalFilename || 'Unknown') === filename).length;
                    const displayName = filename.replace(/\.pdf$/i, '');
                    return (
                      <option key={filename} value={filename}>{displayName} ({count})</option>
                    );
                  })}
                </select>
              </div>
            )}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <svg className="animate-spin h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-neutral-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm text-neutral-600">No sessions found</p>
              </div>
            ) : (
              <div className="space-y-1">
                {sessions
                  .filter(s => pdfFilter === 'all' || (s.originalFilename || 'Unknown') === pdfFilter)
                  .filter(s => matchesSearch(s, searchTerm))
                  .map((session) => (
                  <div
                    key={session.sessionId}
                    className={`p-3 rounded-lg cursor-pointer transition-all duration-150 ${
                      selectedSessionId === session.sessionId 
                        ? 'bg-emerald-100 border-emerald-300 border text-emerald-900' 
                        : 'hover:bg-neutral-50 text-neutral-700 hover:text-neutral-900'
                    }`}
                    onClick={() => setSelectedSessionId(session.sessionId)}
                    onDoubleClick={() => {
                      onLoadSession(session.sessionId);
                      setSelectedSessionId(null);
                    }}
                    onMouseEnter={(e) => handleSessionMouseEnter(session.sessionId, e)}
                    onMouseLeave={handleSessionMouseLeave}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-4 h-4 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="font-medium text-sm truncate">{session.sessionName}</span>
                    </div>

                    {/* Session Description */}
                    {session.description && (
                      <div className="text-xs text-neutral-600 ml-6 mb-1 truncate italic">
                        "{session.description}"
                      </div>
                    )}

                    {/* Latest Change Comment */}
                    {session.changeComment && (
                      <div className="text-xs text-blue-600 ml-6 mb-1 truncate">
                        💬 {session.changeComment}
                      </div>
                    )}

                    {/* PDF filename */}
                    <div className="text-xs text-neutral-400 ml-6 truncate">
                      📄 {session.originalFilename?.replace(/\.pdf$/i, '') || 'Unknown PDF'}
                    </div>

                    {/* Created By */}
                    <div className="text-xs text-neutral-500 ml-6 truncate" title={session.createdByUserEmail || ''}>
                      👤 {session.createdByUserEmail || '—'}
                    </div>

                    <div className="text-xs text-neutral-500 ml-6">
                      {formatTimestamp(session.updatedAt)} • Rev {session.revisionNumber}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Panel: Session Details */}
          <div className="flex-1 p-6 flex flex-col overflow-y-auto">
            {selectedSessionId ? (
              (() => {
                const selectedSession = sessions.find(s => s.sessionId === selectedSessionId);
                if (!selectedSession) return <div className="text-center text-neutral-500 py-8">Session not found</div>;
                
                return (
                  <div className="flex flex-col h-full space-y-4">
                    {/* Session Header */}
                    <div className="border-b border-neutral-200 pb-4">
                      <h2 className="text-xl font-semibold text-neutral-900 mb-2">
                        {selectedSession.sessionName}
                      </h2>
                      
                      {/* Original PDF Filename */}
                      <div className="mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm font-medium text-neutral-600">PDF Source:</span>
                        <span className="text-sm text-neutral-800 bg-neutral-100 px-2 py-1 rounded font-mono">
                          {selectedSession.originalFilename?.replace(/\.pdf\.pdf$/i, '.pdf') || 'Unknown'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getDeploymentStatusStyle(selectedSession.deploymentStatus)}`}>
                          {getDeploymentStatusIcon(selectedSession.deploymentStatus)} {selectedSession.deploymentStatus}
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800 border border-primary-200">
                          Rev {selectedSession.revisionNumber}
                        </span>
                      </div>
                    </div>

                    {/* Session Details */}
                    <div className="flex flex-col flex-1 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-neutral-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-neutral-700 mb-3">Last Updated</h3>
                          <div className="text-lg font-medium text-emerald-600 mb-1">
                            {formatTimestamp(selectedSession.updatedAt)}
                          </div>
                          <div className="text-sm text-neutral-500">
                            {new Date(selectedSession.updatedAt.endsWith('Z') ? selectedSession.updatedAt : selectedSession.updatedAt + 'Z').toLocaleString()}
                          </div>
                          <div className="text-sm text-neutral-500 mt-2 truncate" title={selectedSession.createdByUserEmail || ''}>
                            {selectedSession.createdByUserEmail || '—'}
                          </div>
                        </div>
                        
                        <div className="bg-white border border-neutral-200 rounded-lg p-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-emerald-600 mb-1">
                              {(selectedSession.selectedFields || []).length}
                            </div>
                            <h4 className="text-sm font-semibold text-neutral-700 mb-1">Form Fields</h4>
                            <div className="text-xs text-neutral-500">
                              {(selectedSession.selectedFields || []).length === 1 ? 'field configured' : 'fields configured'}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="bg-white border border-neutral-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-neutral-700 mb-2">Description</h4>
                        <div className="text-sm text-neutral-600 leading-relaxed max-h-24 overflow-y-auto">
                          {selectedSession.description || 'No description provided for this session.'}
                        </div>
                      </div>

                      {/* Latest Change Comment */}
                      {selectedSession.changeComment && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
                            <span>💬</span>
                            <span>Latest Change</span>
                          </h4>
                          <div className="text-sm text-blue-900 leading-relaxed max-h-24 overflow-y-auto">
                            {selectedSession.changeComment}
                          </div>
                        </div>
                      )}

                      {/* Fields List */}
                      <div className="bg-white border border-neutral-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-neutral-700 mb-3">Form Fields ({(selectedSession.selectedFields || []).length})</h4>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {(selectedSession.selectedFields || []).length > 0 ? (
                            selectedSession.selectedFields.map((field: any, index: number) => (
                              <div key={field.id || index} className="flex items-center gap-3 p-2 bg-neutral-50 rounded-lg border border-neutral-200">
                                <div className="flex-shrink-0">
                                  {field.type === 'text' && (
                                    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
                                    </svg>
                                  )}
                                  {field.type === 'checkbox' && (
                                    <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  )}
                                  {field.type === 'radiogroup' && (
                                    <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  )}
                                  {field.type === 'select' && (
                                    <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-neutral-900 truncate">
                                    {field.name || `Field ${index + 1}`}
                                  </div>
                                  <div className="text-xs text-neutral-500 capitalize">
                                    {field.type || 'unknown'}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm text-neutral-500 text-center py-4">No fields configured</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <svg className="mx-auto h-16 w-16 text-neutral-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.121 2.122" />
                  </svg>
                  <p className="text-neutral-600 mb-2">Select a session</p>
                  <p className="text-sm text-neutral-500">Choose a session from the list to view its details</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 flex justify-between">
          <button
            onClick={() => {
              onClose();
              setSelectedSessionId(null);
            }}
            className="px-6 py-2.5 rounded-xl border border-neutral-300 text-neutral-700 hover:bg-neutral-100 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (selectedSessionId) {
                onLoadSession(selectedSessionId);
                setSelectedSessionId(null);
              }
            }}
            disabled={!selectedSessionId}
            className={`px-6 py-2.5 rounded-xl font-medium transition-all duration-200 ${
              selectedSessionId
                ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white hover:from-emerald-700 hover:to-emerald-800'
                : 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
            }`}
          >
            Load Session
          </button>
        </div>

        {/* Resize Handle */}
        <div
          className="absolute bottom-0 right-0 w-6 h-6 cursor-nw-resize opacity-50 hover:opacity-100 transition-opacity"
          onMouseDown={handleMouseDown}
          style={{
            background: 'linear-gradient(-45deg, transparent 30%, #d1d5db 30%, #d1d5db 50%, transparent 50%, transparent 80%, #d1d5db 80%)',
            borderRadius: '0 0 16px 0'
          }}
        />

        {/* Custom Tooltip */}
        {hoveredSession && (() => {
          const session = sessions.find(s => s.sessionId === hoveredSession);
          const hasDescription = (session?.description || '').trim();
          const hasChangeComment = (session?.changeComment || '').trim();

          return session ? (
            <div
              className="fixed z-[60] bg-neutral-800 text-white px-3 py-2 rounded-lg shadow-lg max-w-xs pointer-events-none"
              style={{
                left: tooltipPosition.x,
                top: tooltipPosition.y,
                fontSize: '14px'
              }}
            >
              <div className="mb-2 pb-2 border-b border-neutral-600">
                <div className="font-medium text-sm">{session.sessionName}</div>
                <div className="text-sm text-neutral-300 mt-1">{session.createdByUserEmail || '—'}</div>
                <div className="text-sm text-neutral-300">
                  {formatTimestamp(session.updatedAt)} • Rev {session.revisionNumber}
                </div>
              </div>
              {hasDescription && (
                <div className="mb-2">
                  <div className="font-medium text-sm mb-1">Description:</div>
                  <div className="text-sm leading-relaxed">{session.description}</div>
                </div>
              )}
              {hasChangeComment && (
                <div>
                  <div className="font-medium text-sm mb-1">Latest Change:</div>
                  <div className="text-sm leading-relaxed text-blue-300">💬 {session.changeComment}</div>
                </div>
              )}
            </div>
          ) : null;
        })()}
      </div>
    </div>
  );
};

export default LoadSessionDialog;